/**
 * Mini portfolio under the compact Markets list on a desktop (#2242 rev 3): the Portfolio page's tabs and panels in the
 * bottom part of the screen, each scrolling inside, with a link to the full page. The data is the user session's,
 * held above the routes (#1809): showing it here reads and subscribes nothing the Portfolio page would not.
 * Without an account: one line with a Connect button.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { isPortfolioTab, PortfolioTabList, PortfolioTabPanel, type PortfolioTabId } from '../account/PortfolioTabs';
import { GhostDialog, WalletPickerModal } from '../components/WalletModals';
import { shortAddress } from '../wallet/address';
import { useViewedAccount, useWallet } from '../wallet/context';
import { useT } from '../i18n/useT';

/** The tab last picked here, per browser (the Portfolio page keeps its own in the URL). */
export const MINI_TAB_STORAGE_KEY = 'outfox.portfolio.mini_tab';

function loadTab(): PortfolioTabId {
  try {
    const v = localStorage.getItem(MINI_TAB_STORAGE_KEY);
    return isPortfolioTab(v) ? v : 'balances';
  } catch {
    return 'balances';
  }
}

function saveTab(v: PortfolioTabId): void {
  try {
    localStorage.setItem(MINI_TAB_STORAGE_KEY, v);
  } catch {
    /* storage unavailable: the choice lasts until the page closes */
  }
}

function pageHref(tab: PortfolioTabId, ghost: string | null): string {
  const q = new URLSearchParams();
  if (tab !== 'balances') q.set('tab', tab);
  if (ghost) q.set('ghost', ghost);
  const s = q.toString();
  return `/portfolio${s ? `?${s}` : ''}`;
}

const LINK = 'shrink-0 whitespace-nowrap rounded-md border border-border px-2.5 py-1 text-xs font-medium text-accent hover:bg-surface-2';

export default function MiniPortfolio({ className = '' }: { className?: string }) {
  const t = useT();
  const { address, mode } = useViewedAccount();
  const wallet = useWallet();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<PortfolioTabId>(loadTab);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [ghostOpen, setGhostOpen] = useState(false);
  const select = (id: PortfolioTabId) => {
    setTab(id);
    saveTab(id);
  };
  const href = pageHref(tab, params.get('ghost'));
  const goFull = (
    <Link to={href} className={LINK} data-testid="mini-portfolio-page">
      {t('portfolio.mini.go_full')}
    </Link>
  );

  if (!address) {
    const busy = wallet.status === 'connecting' || wallet.status === 'reconnecting';
    return (
      <section
        aria-label={t('nav.portfolio')}
        data-testid="mini-portfolio"
        data-empty="true"
        className={`card flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 text-sm ${className}`}
      >
        <h2 className="font-semibold">{t('nav.portfolio')}</h2>
        <p className="min-w-0 flex-1 text-muted" data-testid="mini-portfolio-empty">
          {t('portfolio.mini.connect')}
        </p>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={busy}
          className="shrink-0 rounded-md bg-accent px-3 py-1 text-xs font-semibold text-bg disabled:opacity-60"
          data-testid="mini-portfolio-connect"
        >
          {busy ? t('wallet.connecting') : t('wallet.connect')}
        </button>
        {goFull}
        <WalletPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onGhost={() => {
            setPickerOpen(false);
            setGhostOpen(true);
          }}
        />
        <GhostDialog open={ghostOpen} onClose={() => setGhostOpen(false)} />
      </section>
    );
  }

  return (
    <section
      aria-label={t('nav.portfolio')}
      data-testid="mini-portfolio"
      className={`card flex min-h-0 flex-col overflow-hidden ${className}`}
    >
      <div className="flex items-center gap-3 border-b border-border px-3 py-1.5">
        <h2 className="shrink-0 text-sm font-semibold">{t('nav.portfolio')}</h2>
        <PortfolioTabList tab={tab} onSelect={select} idPrefix="mini-portfolio" small className="min-w-0 overflow-x-auto" />
        <span className="num ms-auto hidden truncate text-xs text-muted xl:inline" data-testid="mini-portfolio-account">
          {mode === 'ghost' ? t('portfolio.watching', { address: shortAddress(address) }) : shortAddress(address)}
        </span>
        <span className="ms-auto xl:hidden" />
        {goFull}
      </div>
      {/* key={address}: switching account remounts the panel so no stale rows flash (as on the page). */}
      <div key={address} className="min-h-0 flex-1 overflow-y-auto p-2" data-testid="mini-portfolio-body">
        <PortfolioTabPanel tab={tab} address={address} idPrefix="mini-portfolio" />
      </div>
    </section>
  );
}
